import { useDescope, useUser } from '@descope/react-sdk'
import { ChevronDown } from 'lucide-react'
import { useMemo } from 'react'

import { useOrgDetails } from '@/lib/auth/orgid.hook'
import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import { AppRoute } from '@/lib/url/urls'

import { CommandSwitcher } from '@/components/lemmala-design-system/command-switcher/command-switcher'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

interface SidebarTenantSwitcherProps {
  expanded: boolean
}

export const SidebarTenantSwitcher = ({ expanded }: SidebarTenantSwitcherProps) => {
  const { toast } = useToast()
  const { user } = useUser()
  const descope = useDescope()
  const { orgDetails } = useOrgDetails()
  const reportErrors = useReportErrorsCallback()

  const tenants = useMemo(() => {
    if (!user?.userTenants) return []
    return user.userTenants
      .map((tenant) => ({
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName || tenant.tenantId,
      }))
      .sort((a, b) => a.tenantName.localeCompare(b.tenantName))
  }, [user])

  if (!user || !user.userTenants || user.userTenants.length <= 1) return null
  if (!expanded) return <div className='h-8' aria-hidden />

  const currentTenantName = orgDetails?.orgName || 'Organization'
  const currentTenantId = orgDetails?.orgId || ''

  const handleTenantSelect = async (tenantId: string) => {
    try {
      await descope.selectTenant(tenantId)
      // Full reload onto the overview page: clears all cached state (queries, auth tokens, etc.)
      // and drops any old-tenant IDs in the current URL, which the new tenant can't resolve.
      // No success toast — the reload tears the page down before it could ever be seen; the
      // switched-to org name in the sidebar is the confirmation.
      window.location.assign(AppRoute.OVERVIEW)
    } catch (error) {
      reportErrors(error instanceof Error ? error : new Error('Failed to switch tenant'))
      toast({
        variant: 'destructive',
        title: 'Failed to switch tenant.',
        description: 'Please try again or contact support if the issue persists',
      })
    }
  }

  const commandOptions = tenants.map((tenant) => ({
    id: tenant.tenantId,
    label: tenant.tenantName,
  }))

  return (
    <CommandSwitcher
      options={commandOptions}
      selectedId={currentTenantId}
      selectedLabel={currentTenantName}
      onSelect={handleTenantSelect}
      placeholder='Search tenant...'
      emptyMessage='No tenant found.'
      buttonClassName='w-full justify-between px-2 text-left'
      triggerContent={
        <>
          <Tooltip
            asChild
            trigger={
              <span className='min-w-0 flex-1 overflow-hidden'>
                <span className='block truncate text-main-foreground-secondary text-label-default'>
                  {currentTenantName}
                </span>
              </span>
            }
          >
            {currentTenantName}
          </Tooltip>
          <ChevronDown className='size-4 shrink-0 text-main-foreground-secondary' />
        </>
      }
    />
  )
}
